import { SpanStatusCode } from '@opentelemetry/api';
import {
  ATTR_HTTP_REQUEST_METHOD,
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_HTTP_ROUTE,
  ATTR_URL_FULL,
  ATTR_URL_PATH,
  ATTR_URL_QUERY,
  ATTR_URL_SCHEME,
} from '@opentelemetry/semantic-conventions';
import { createMiddleware } from '@tanstack/react-start';

import { name } from '../../package.json';

import type { RouteFullPaths } from '@/types/routes';
import { isPresent } from '@/utils/helpers';
import { isDefaultRoute, routeMatches } from '@/utils/routes';
import { createTracer } from '@/utils/tracer';

const tracer = createTracer(name);

function isTraceableRoute(route: RouteFullPaths | undefined): route is RouteFullPaths {
  return isPresent(route) && !isDefaultRoute(route);
}

function isApiRoute(path: string) {
  return path.startsWith('/api');
}

function isServerFnRoute(path: string) {
  return path.startsWith('/_serverFn');
}

export const traceMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  const spanName = `${request.method} ${url.pathname}`;

  const matchedRoute = routeMatches(url.pathname)?.at(-1)?.fullPath as RouteFullPaths | undefined;

  if (!isApiRoute(url.pathname) && !isServerFnRoute(url.pathname) && !isTraceableRoute(matchedRoute)) {
    return next();
  }

  return tracer.startActiveSpan(spanName, async (span) => {
    span.setAttributes({
      [ATTR_HTTP_REQUEST_METHOD]: request.method,
      [ATTR_URL_FULL]: request.url,
      [ATTR_URL_PATH]: url.pathname,
      [ATTR_URL_SCHEME]: url.protocol.replace(':', ''),
      [ATTR_URL_QUERY]: url.search,
    });

    if (isTraceableRoute(matchedRoute)) {
      span.setAttribute(ATTR_HTTP_ROUTE, matchedRoute);
    }

    try {
      const result = await next();

      const { status, statusText } = result.response;

      span.setAttribute(ATTR_HTTP_RESPONSE_STATUS_CODE, status);
      span.setStatus({ code: SpanStatusCode.OK });

      const prefix = `[${status}${statusText.length > 0 ? ` ${statusText}` : ''}]`;

      if (isApiRoute(url.pathname)) {
        console.info(`${prefix}[API][${request.method.toUpperCase()}] ${url.pathname.replace('/api', '')}`);
      }

      if (isServerFnRoute(url.pathname)) {
        console.info(`${prefix}[SERVER FN][${request.method.toUpperCase()}] ${url.pathname.replace('/_serverFn', '')}`);
      }

      if (!isApiRoute(url.pathname) && !isServerFnRoute(url.pathname) && isTraceableRoute(matchedRoute)) {
        console.info(`${prefix}[ROUTE LOAD][${request.method.toUpperCase()}] ${url.pathname}`);
      }

      return result;
    } catch (error) {
      span.recordException(error as Error);
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: (error as Error).message,
      });

      throw error;
    } finally {
      span.end();
    }
  });
});

export const traceFunctionMiddleware = createMiddleware({ type: 'function' }).server(
  async ({ next, method, serverFnMeta }) => {
    const spanName = `ServerFn: ${serverFnMeta.name}`;

    return tracer.startActiveSpan(spanName, async (span) => {
      span.setAttributes({
        [ATTR_HTTP_REQUEST_METHOD]: method,
        'server.function.name': serverFnMeta.name,
      });

      try {
        const result = await next();

        span.setStatus({ code: SpanStatusCode.OK });

        console.info(`[SERVER FN][${method.toUpperCase()}] ${serverFnMeta.name}`);

        return result;
      } catch (error) {
        span.recordException(error as Error);
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: (error as Error).message,
        });

        throw error;
      } finally {
        span.end();
      }
    });
  },
);
